import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { useOnboarding } from '../../context/OnboardingContext';
import { saveOnboardingData } from '../../services/onboardingApi';

// ---------- Inline animation styles ----------
const styles = `
  .onb-comp { font-family: 'Poppins', system-ui, sans-serif; position: relative; min-height: 100vh; background: #092825; color: #fff; overflow: hidden; }
  .onb-comp::before {
    content: ''; position: absolute; inset: 0;
    background:
      radial-gradient(700px 500px at 50% -15%, rgba(47, 161, 99, 0.45) 0%, transparent 60%),
      radial-gradient(500px 400px at 100% 100%, rgba(127, 211, 166, 0.20) 0%, transparent 65%),
      linear-gradient(180deg, #092825 0%, #0e3826 60%, #14583a 100%);
    z-index: 0;
  }
  .onb-content {
    position: relative; z-index: 1;
    padding: 56px 28px 32px; min-height: 100vh;
    display: flex; flex-direction: column; align-items: center; text-align: center;
    transition: opacity 0.7s ease, transform 0.7s ease;
    max-width: 480px; margin: 0 auto;
  }
  .onb-content.is-departing { opacity: 0; transform: scale(0.96); }

  .onb-fairy-stage { position: relative; width: 200px; height: 160px; margin: 0 0 20px; display: flex; align-items: center; justify-content: center; }
  .onb-halo {
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: 170px; height: 170px; border-radius: 50%;
    background: radial-gradient(circle at 50% 50%, rgba(127, 211, 166, 0.55) 0%, rgba(47, 161, 99, 0.25) 40%, transparent 72%);
    filter: blur(14px);
    animation: onbHaloBreath 4.2s ease-in-out infinite;
    z-index: 0;
  }
  @keyframes onbHaloBreath { 0%, 100% { opacity: 0.55; transform: translate(-50%, -50%) scale(0.92); } 50% { opacity: 0.85; transform: translate(-50%, -50%) scale(1.08); } }
  .onb-halo.is-celebrate { animation: onbHaloCelebrate 1.8s ease-out forwards; }
  @keyframes onbHaloCelebrate { 0% { opacity: 0.85; transform: translate(-50%, -50%) scale(1); } 40% { opacity: 1; transform: translate(-50%, -50%) scale(1.4); background: radial-gradient(circle at 50% 50%, rgba(186, 240, 200, 0.95) 0%, rgba(127, 211, 166, 0.55) 40%, transparent 78%); } 100% { opacity: 0.7; transform: translate(-50%, -50%) scale(1.18); } }
  .onb-halo.is-burst { animation: onbHaloBurst 1.6s ease-out forwards; }
  @keyframes onbHaloBurst { 0% { opacity: 0.85; transform: translate(-50%, -50%) scale(1.2); background: radial-gradient(circle at 50% 50%, rgba(186, 240, 200, 0.95) 0%, rgba(127, 211, 166, 0.55) 40%, transparent 78%); } 35% { opacity: 1; transform: translate(-50%, -50%) scale(3.4); background: radial-gradient(circle at 50% 50%, rgba(255,255,255,1) 0%, rgba(186, 240, 200, 0.85) 35%, rgba(127, 211, 166, 0.40) 70%, transparent 90%); } 100% { opacity: 0; transform: translate(-50%, -50%) scale(5.5); } }

  .onb-fairy { position: relative; z-index: 2; width: 130px; height: 130px; animation: onbFairyEntry 0.95s cubic-bezier(0.34, 1.56, 0.64, 1) both, onbFairyFloat 4.8s ease-in-out infinite 1s; }
  .onb-fairy img { width: 100%; height: 100%; display: block; filter: brightness(0) invert(1) drop-shadow(0 6px 18px rgba(127, 211, 166, 0.45)); }
  .onb-fairy.is-exit { animation: onbFairyExit 1.4s cubic-bezier(0.4, 0, 0.2, 1) forwards !important; }
  @keyframes onbFairyEntry { 0% { transform: scale(0) rotate(-8deg); opacity: 0; } 55% { transform: scale(1.08) rotate(3deg); opacity: 1; } 100% { transform: scale(1) rotate(0); opacity: 1; } }
  @keyframes onbFairyFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
  @keyframes onbFairyExit { 0% { transform: scale(1) translateY(0); opacity: 1; filter: brightness(0) invert(1) drop-shadow(0 6px 18px rgba(127, 211, 166, 0.45)); } 35% { transform: scale(1.18) translateY(-6px); opacity: 1; filter: brightness(0) invert(1) drop-shadow(0 0 32px rgba(255, 255, 255, 0.95)); } 100% { transform: scale(1.45) translateY(-14px); opacity: 0; filter: brightness(0) invert(1) drop-shadow(0 0 50px rgba(255, 255, 255, 1)); } }

  .onb-spark { position: absolute; width: 9px; height: 9px; color: #a3e0c0; z-index: 1; opacity: 0; pointer-events: none; }
  .onb-spark svg { width: 100%; height: 100%; }
  .onb-spark.s1 { top: -4%; left: 12%; animation: onbSpark 3.2s ease-in-out infinite 0.4s; }
  .onb-spark.s2 { top: 6%; left: 84%; animation: onbSpark 3.2s ease-in-out infinite 1.0s; width: 11px; height: 11px; }
  .onb-spark.s3 { top: 60%; left: 2%; animation: onbSpark 3.2s ease-in-out infinite 1.6s; width: 7px; height: 7px; }
  .onb-spark.s4 { top: 70%; left: 88%; animation: onbSpark 3.2s ease-in-out infinite 0.8s; width: 10px; height: 10px; }
  .onb-spark.s5 { top: 32%; left: 95%; animation: onbSpark 3.2s ease-in-out infinite 2.0s; width: 8px; height: 8px; }
  .onb-spark.s6 { top: 92%; left: 38%; animation: onbSpark 3.2s ease-in-out infinite 1.3s; width: 9px; height: 9px; color: #fff; }
  .onb-spark.s7 { top: 18%; left: -2%; animation: onbSpark 3.2s ease-in-out infinite 2.4s; width: 6px; height: 6px; color: #fff; }
  @keyframes onbSpark { 0%, 100% { opacity: 0; transform: scale(0.4) rotate(0); } 25% { opacity: 1; transform: scale(1) rotate(45deg); } 50% { opacity: 0.6; transform: scale(0.85) rotate(180deg); } 75% { opacity: 0; transform: scale(0.4) rotate(270deg); } }

  .onb-burst { position: absolute; top: 50%; left: 50%; width: 12px; height: 12px; color: #fff; opacity: 0; pointer-events: none; z-index: 3; margin: -6px 0 0 -6px; }
  .onb-burst svg { width: 100%; height: 100%; }
  .onb-burst.is-go { animation: onbBurstFly 1.6s cubic-bezier(0.2, 0.7, 0.3, 1) forwards; }
  @keyframes onbBurstFly { 0% { opacity: 0; transform: rotate(var(--angle)) translateX(0) scale(0.4); } 25% { opacity: 1; } 100% { opacity: 0; transform: rotate(var(--angle)) translateX(var(--distance, 180px)) scale(1); } }

  .onb-eyebrow { font-size: 11px; font-weight: 600; letter-spacing: 0.22em; color: #6ee7a8; margin: 0 0 10px; text-transform: uppercase; transition: opacity 0.65s ease; height: 14px; line-height: 14px; }
  .onb-msg-wrap { height: 130px; margin-bottom: 26px; display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; }
  .onb-msg { font-family: 'Poppins', sans-serif; font-size: 22px; font-weight: 500; line-height: 1.3; letter-spacing: -0.01em; color: #fff; margin: 0 0 8px; max-width: 320px; transition: opacity 0.65s ease; }
  .onb-msg-sub { font-size: 12.5px; color: rgba(255,255,255,0.65); margin: 0; line-height: 1.5; transition: opacity 0.65s ease; max-width: 290px; }
  .is-fade-out { opacity: 0; }

  .onb-list { align-self: stretch; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; padding: 16px; margin-bottom: 24px; text-align: left; }
  .onb-list-title { font-size: 10.5px; font-weight: 600; letter-spacing: 0.14em; color: rgba(255,255,255,0.55); text-transform: uppercase; margin: 0 0 12px; }
  .onb-list-item { display: flex; align-items: center; gap: 10px; padding: 7px 0; font-size: 13px; transition: color 0.5s ease; color: rgba(255,255,255,0.42); }
  .onb-check { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: background 0.5s ease, border-color 0.5s ease; position: relative; background: transparent; border: 1px solid rgba(255,255,255,0.20); color: transparent; }
  .onb-check svg { width: 12px; height: 12px; transition: opacity 0.5s ease, transform 0.5s ease; opacity: 0; transform: scale(0.5); }
  .onb-list-item.is-active .onb-check { background: rgba(127, 211, 166, 0.18); border-color: #6ee7a8; color: #6ee7a8; }
  .onb-list-item.is-active { color: #fff; font-weight: 500; }
  .onb-list-item.is-active .onb-spinner { display: block; }
  .onb-list-item.is-done .onb-check { background: #2FA163; border-color: #2FA163; color: #fff; }
  .onb-list-item.is-done .onb-check svg { opacity: 1; transform: scale(1); }
  .onb-list-item.is-done { color: #fff; }
  .onb-list-item.is-done .onb-spinner { display: none; }
  .onb-spinner { width: 12px; height: 12px; border: 1.5px solid rgba(127, 211, 166, 0.30); border-top-color: #6ee7a8; border-radius: 50%; animation: onbSpin 1s linear infinite; display: none; position: absolute; }
  @keyframes onbSpin { to { transform: rotate(360deg); } }

  .onb-progress { display: flex; gap: 6px; justify-content: center; padding-bottom: 8px; }
  .onb-dot { width: 28px; height: 4px; border-radius: 2px; background: rgba(255,255,255,0.18); transition: background 0.6s ease; }
  .onb-dot.is-active { background: #6ee7a8; }
  .onb-dot.is-done { background: #2FA163; }
`;

const FAIRY_URL = 'https://customer-assets.emergentagent.com/job_agent-portal-27/artifacts/azdcmpew_Logo_with_brown_background-removebg-preview.png';

const BURST_ANGLES = [
  { a: 0,   d: 180 }, { a: 30,  d: 160 }, { a: 60,  d: 200 }, { a: 90,  d: 170 },
  { a: 120, d: 190 }, { a: 150, d: 175 }, { a: 180, d: 180 }, { a: 210, d: 165 },
  { a: 240, d: 210 }, { a: 270, d: 185 }, { a: 300, d: 195 }, { a: 330, d: 175 },
];

const STATES = [
  { eyebrow: 'SETTING UP YOUR DASHBOARD', msg: 'The platform is taking in all your information and creating a custom dashboard.', sub: 'Pulling your goals, commission structure, and weekly focus into one place.', items: ['active','pending','pending','pending','pending'], dots: ['active','pending','pending'], phase: 'main', hold: 2500 },
  { eyebrow: 'SETTING UP YOUR DASHBOARD', msg: 'The platform is taking in all your information and creating a custom dashboard.', sub: 'Pulling your goals, commission structure, and weekly focus into one place.', items: ['done','active','pending','pending','pending'], dots: ['active','pending','pending'], phase: 'main', hold: 2500 },
  { eyebrow: 'LEARNING YOUR VOICE', msg: 'The AI is learning your preferences.', sub: 'Building your personalized coaching context from what you just told us.', items: ['done','done','active','pending','pending'], dots: ['done','active','pending'], phase: 'main', hold: 2500 },
  { eyebrow: 'LEARNING YOUR VOICE', msg: 'The AI is learning your preferences.', sub: 'Building your personalized coaching context from what you just told us.', items: ['done','done','done','pending','pending'], dots: ['done','active','pending'], phase: 'main', hold: 2500 },
  { eyebrow: 'BUILDING YOUR PLAN', msg: 'The system is developing a custom plan that looks at each day.', sub: 'Mapping your weekly focus to daily priorities the coach will surface.', items: ['done','done','done','active','pending'], dots: ['done','done','active'], phase: 'main', hold: 2500 },
  { eyebrow: 'BUILDING YOUR PLAN', msg: 'The system is developing a custom plan that looks at each day.', sub: 'Mapping your weekly focus to daily priorities the coach will surface.', items: ['done','done','done','done','active'], dots: ['done','done','active'], phase: 'main', hold: 2500 },
  { eyebrow: 'READY', msg: 'Welcome to I Need Numbers.', sub: 'Your dashboard is ready.', items: ['done','done','done','done','done'], dots: ['done','done','done'], phase: 'finale', hold: 2300 },
  { eyebrow: 'READY', msg: 'Welcome to I Need Numbers.', sub: 'Your dashboard is ready.', items: ['done','done','done','done','done'], dots: ['done','done','done'], phase: 'exit', hold: 1500 },
];

const CHECKLIST = [
  'Your income goal & commission structure',
  'Your weekly hours & focus areas',
  'Your active deals & milestones',
  'Your daily activity patterns',
  'Your social voice preferences',
];

const CheckIcon = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
);
const SparkIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 0l1.5 8.5L22 10l-8.5 1.5L12 20l-1.5-8.5L2 10l8.5-1.5z"/></svg>
);

const CompletionScreen = () => {
  const navigate = useNavigate();
  const { onboardingData } = useOnboarding();
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const timersRef = useRef([]);

  // Save onboarding data, then fire the onboarding-complete API call on mount
  useEffect(() => {
    const completeOnboarding = async () => {
      try {
        // First, save the full onboarding profile
        console.log('[CompletionScreen] Saving onboarding data:', onboardingData);
        await saveOnboardingData(onboardingData);
        console.log('[CompletionScreen] Onboarding data saved successfully');
        
        // Then mark onboarding as complete (this triggers goal/cap initialization)
        await axios.post(`${API_BASE_URL}/api/onboarding/complete`, {}, { withCredentials: true });
        console.log('[CompletionScreen] Onboarding marked complete');
      } catch (err) {
        console.warn('[CompletionScreen] onboarding API failed (non-blocking):', err?.message || err);
      }
    };
    
    completeOnboarding();
  }, [onboardingData]);

  // Drive the animation state machine
  useEffect(() => {
    const cur = STATES[idx];
    if (!cur) return;

    // Schedule next state transition
    const nextTimer = setTimeout(() => {
      if (idx < STATES.length - 1) {
        // Trigger crossfade if the message changed
        const prev = STATES[idx];
        const next = STATES[idx + 1];
        const msgChanged = prev.msg !== next.msg || prev.eyebrow !== next.eyebrow;
        if (msgChanged && next.phase !== 'exit') {
          setFading(true);
          setTimeout(() => {
            setIdx(idx + 1);
            setFading(false);
          }, 620);
        } else {
          setIdx(idx + 1);
        }
      } else {
        // After the exit state finishes, navigate to dashboard
        navigate('/dashboard');
      }
    }, cur.hold);
    timersRef.current.push(nextTimer);

    return () => {
      timersRef.current.forEach(t => clearTimeout(t));
      timersRef.current = [];
    };
  }, [idx, navigate]);

  const state = STATES[idx];
  const inFinale = state.phase === 'finale';
  const inExit = state.phase === 'exit';

  return (
    <>
      <style>{styles}</style>
      <div className="onb-comp">
        <div className={`onb-content ${inExit ? 'is-departing' : ''}`}>

          <div className="onb-fairy-stage">
            <div className={`onb-halo ${inFinale ? 'is-celebrate' : ''} ${inExit ? 'is-burst' : ''}`} />

            {/* Ambient sparkles */}
            <div className="onb-spark s1"><SparkIcon /></div>
            <div className="onb-spark s2"><SparkIcon /></div>
            <div className="onb-spark s3"><SparkIcon /></div>
            <div className="onb-spark s4"><SparkIcon /></div>
            <div className="onb-spark s5"><SparkIcon /></div>
            <div className="onb-spark s6"><SparkIcon /></div>
            <div className="onb-spark s7"><SparkIcon /></div>

            {/* Burst sparkles fire during exit */}
            {BURST_ANGLES.map(({ a, d }, i) => (
              <div key={i} className={`onb-burst ${inExit ? 'is-go' : ''}`} style={{ '--angle': `${a}deg`, '--distance': `${d}px` }}>
                <SparkIcon />
              </div>
            ))}

            <div className={`onb-fairy ${inExit ? 'is-exit' : ''}`}>
              <img alt="I Need Numbers" src={FAIRY_URL} />
            </div>
          </div>

          <p className={`onb-eyebrow ${fading ? 'is-fade-out' : ''}`}>{state.eyebrow}</p>
          <div className="onb-msg-wrap">
            <p className={`onb-msg ${fading ? 'is-fade-out' : ''}`}>{state.msg}</p>
            <p className={`onb-msg-sub ${fading ? 'is-fade-out' : ''}`}>{state.sub}</p>
          </div>

          <div className="onb-list">
            <p className="onb-list-title">YOUR COACH WILL READ</p>
            {CHECKLIST.map((label, i) => (
              <div key={i} className={`onb-list-item ${state.items[i] === 'active' ? 'is-active' : ''} ${state.items[i] === 'done' ? 'is-done' : ''}`}>
                <div className="onb-check">
                  <CheckIcon />
                  <div className="onb-spinner" />
                </div>
                {label}
              </div>
            ))}
          </div>

          <div className="onb-progress" style={{ marginTop: 'auto' }}>
            {state.dots.map((s, i) => (
              <div key={i} className={`onb-dot ${s === 'active' ? 'is-active' : ''} ${s === 'done' ? 'is-done' : ''}`} />
            ))}
          </div>

        </div>
      </div>
    </>
  );
};

export default CompletionScreen;
